import { ForwardedRef, forwardRef, ForwardRefRenderFunction, useImperativeHandle, useRef, useState } from 'react';
import { EVideoMuteStatus } from 'shared/models';
import SimpleToast from '../simple-toast';
import { useTranslation } from 'react-i18next';

export interface SimpleVideoProps {
  className?: string;

  onClick?: () => void;

  /**
   * @default false
   */
  loop?: boolean;

  /**
   * @default false
   */
  autoPlay?: boolean;

  videoSrc: string | string[];

  posterSrc: string;

  muteStatus: EVideoMuteStatus;
}

export enum VideoStatus {
  INITIAL = 'initial',
  PLAYING = 'playing',
  PAUSED = 'paused',
  ENDED = 'ended',
}

export interface SimpleVideoForwardProps {
  video: HTMLVideoElement;

  seek: (time: number) => void;

  play: () => void;

  pause: () => void;

  mute: () => void;

  unmute: () => void;

  status: VideoStatus;

  muted: boolean;

  addEventListener: HTMLVideoElement['addEventListener'];
  removeEventListener: HTMLVideoElement['removeEventListener'];
}

/**
 * 支持 SSR 的 video 组件
 */
const SimpleVideo: ForwardRefRenderFunction<SimpleVideoForwardProps, SimpleVideoProps> = (
  props: SimpleVideoProps,
  ref: ForwardedRef<SimpleVideoForwardProps>,
) => {
  const { className, onClick, videoSrc, posterSrc, autoPlay = false, loop = false, muteStatus } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState(VideoStatus.INITIAL);
  const [muted, setMuted] = useState(true);
  const [toast, setToast] = useState({ show: false, content: '' });
  const { t } = useTranslation();

  useImperativeHandle<SimpleVideoForwardProps, SimpleVideoForwardProps>(
    ref,
    () => ({
      video: videoRef.current as HTMLVideoElement,
      seek: (time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      play: () => {
        videoRef.current?.play().catch(() => void 0);
      },
      pause: () => {
        videoRef.current?.pause();
      },
      mute: () => {
        if (videoRef.current) {
          setMuted(true);
          videoRef.current.muted = true;
        }
      },
      unmute: () => {
        if (videoRef.current) {
          if (muteStatus === EVideoMuteStatus.MUTED) {
            setToast({ show: true, content: t('video_muted_toast') });
            return;
          }
          setMuted(false);
          videoRef.current.muted = false;
        }
      },
      status,
      muted,
      addEventListener: (
        p0: Parameters<HTMLVideoElement['addEventListener']>[0],
        p1: Parameters<HTMLVideoElement['addEventListener']>[1],
        p2: Parameters<HTMLVideoElement['addEventListener']>[2],
      ) => {
        videoRef.current?.addEventListener(p0, p1, p2);
      },
      removeEventListener: (
        p0: Parameters<HTMLVideoElement['removeEventListener']>[0],
        p1: Parameters<HTMLVideoElement['removeEventListener']>[1],
        p2: Parameters<HTMLVideoElement['removeEventListener']>[2],
      ) => {
        videoRef.current?.removeEventListener(p0, p1, p2);
      },
    }),
    [videoRef, status, muted, muteStatus],
  );

  const handlePlayEvent = (): void => {
    setStatus(VideoStatus.PLAYING);
  };

  const handlePauseEvent = (): void => {
    setStatus(VideoStatus.PAUSED);
  };

  const handleEndEvent = (): void => {
    setStatus(VideoStatus.ENDED);
  };

  return (
    <>
      <video
        muted
        playsInline
        loop={loop}
        ref={videoRef}
        poster={posterSrc}
        className={className}
        autoPlay={autoPlay}
        onClick={onClick}
        onPause={handlePauseEvent}
        onPlay={handlePlayEvent}
        onEnded={handleEndEvent}
      >
        {typeof videoSrc === 'string' && <source src={videoSrc} />}
        {videoSrc instanceof Array && videoSrc.map((src) => <source key={src} src={src} />)}
      </video>
      <SimpleToast show={toast.show} setShow={(show) => setToast({ show, content: '' })} content={toast.content} />
    </>
  );
};

export default forwardRef<SimpleVideoForwardProps, SimpleVideoProps>(SimpleVideo);
